"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

import { IconCalendarWeek, IconHomeOff } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import toast from "react-hot-toast";

import { useContextMenuActions } from "@/components/widgets/Realtor/RealtorContextMenu/hooks/useContextMenuActions";
import { DealType } from "@/enums/dealType";
import { FeatureFlags } from "@/enums/feature-flags";
import { EnumViewMode } from "@/enums/view-mode";
import { useArchiveControl } from "@/hooks/useArchiveControl";
import { useReturnPath } from "@/hooks/useReturnPath";
import { useUserRole } from "@/hooks/useUserRole";
import { Link } from "@/i18n/routing";
import IconSquare from "@/icons/content/property/area.svg";
import IconId from "@/icons/ui/forms/icon_id.svg";
import { useCurrencyStore } from "@/store/currency.store";
import { ConvertedPrices, PricingStructure } from "@/types/pricing.types";
import { PropertyStatus } from "@/types/property-status";
import { ViewType } from "@/types/search.types";
import { AddToFavorite } from "@/ui/Common/AddToFavorite";
import { Amount, AmountVariant } from "@/ui/Common/Amount";
import { CustomSlider } from "@/ui/Common/CustomSlider";
import LocationTitle from "@/ui/Common/LocationTitle";
import { ExpandButton } from "@/ui/Common/ShowMore";
import { cn } from "@/utils/cn";
import { formatDate } from "@/utils/formatDate";
import { CallButton } from "@/widgets/Call";
import { CurrencyChanger } from "@/widgets/CurrencyChanger";
import { PropertyMainInfo } from "@/widgets/Property/MainInfo";
import { WriteButton } from "@/widgets/Write";

import iconEmpty from "public/assets/img/empty.png";

import VisaIcon from "@/icons/visa/goldenVisa.svg";

import { IImagePopulated, IImagesProperty } from "@/types/images.types";
import { Feature } from "@/ui/Feature";
import ObjectActionsMenu from "@/ui/ObjectActionsMenu/ObjectActionsMenu";
import { getFallbackSrc } from "@/utils/buildSrcSet";
import ResponsiveImage from "../../ResponsiveImage";
import styles from "./styles.module.scss";
import StatusBadge from "@/widgets/Badges/StatusBadge";
import { PublicationStatus } from "@/types/enums";

// Проверка, выполняется ли код в браузере
const isBrowser =
  typeof window !== "undefined" &&
  typeof File !== "undefined" &&
  typeof URL !== "undefined";

// Image format types
interface OptimizedImageObject {
  id: string;
  original: string;
  mobile: string;
  tablet: string;
  desktop: string;
  large: string;
  mobileWebp: string;
  tabletWebp: string;
  desktopWebp: string;
  largeWebp: string;
}

interface Props extends React.HTMLAttributes<HTMLLIElement> {
  title: string;
  view?: ViewType;
  pricing: PricingStructure;
  location: Record<string, string> | string;
  area: number | string;
  areaMin?: number | string;
  areaMax?: number | string;
  landArea?: number | string;
  images: IImagesProperty[];
  id: string;
  slug: string;
  redirectLink: string;
  dealType?: DealType;
  description?: string;
  floor?: number;
  floors?: number;
  createdAt?: string;
  bedrooms?: number;
  rooms?: number;
  programs?: { isGoldenVisaEligible?: boolean };
  customId?: string;
  status?: PublicationStatus | string;
  translations?: Array<{
    language: string;
    title: string;
    description: string;
  }>;
  isAvalibleVisa?: boolean;
  canArchive?: boolean;
  isFavorite?: boolean;
}

// Fallback image component
const FallbackImage = ({ className }: { className?: string }) => (
  <div
    className={cn("flex items-center justify-center bg-gray-100", className)}
  >
    <IconHomeOff
      size={100}
      stroke={1}
      className="text-gray-200"
    />
  </div>
);

// TODO currency isLoading =>skeleton

export const ObjectCard = ({
  title,
  view = EnumViewMode.LIST,
  pricing,
  location,
  area,
  areaMin,
  areaMax,
  images,
  id,
  slug,
  redirectLink,
  description,
  floor,
  floors,
  bedrooms,
  rooms,
  dealType = DealType.PRIMARY,
  createdAt,
  customId,
  status,
  className,
  translations,
  programs,
  isAvalibleVisa,
  landArea,
  canArchive = false,
  isFavorite = false,
}: Props) => {
  const t = useTranslations();
  const { currency } = useCurrencyStore();
  const { getObjectLink } = useReturnPath();
  const { hasEnhancedAccess } = useUserRole();
  const locale = useLocale();

  const objectTitle =
    translations?.find((item) => item.language === locale)?.title || title;

  const objectDescription =
    translations?.find((item) => item.language === locale)?.description ||
    description;

  const {
    actualizeProperty,
    duplicateProperty,
    shareProperty,
    deleteProperty,
    isActualizing,
    isDuplicating,
    isDeleting,
  } = useContextMenuActions();

  const [showMore, setShowMore] = useState<boolean>(true);
  const [showMoreState, setShowMoreState] = useState<boolean>(true);

  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // // Обработка изображений
  // const slicedImages = useMemo(() => {
  //   if (!images || images.length === 0) {
  //     return [];
  //   }

  //   const processedImages = images
  //     .map((image) => {
  //       // Check if image is an object with optimized variants
  //       if (typeof image === "object" && image !== null && "tablet" in image) {
  //         // Use mobile (320px) for small screens, tablet (640px) for larger screens
  //         // Browser will choose based on sizes attribute
  //         const imageUrl = image.tablet || image.original || image.desktop;
  //         return imageUrl;
  //       }

  //       // Handle File objects (for uploads)
  //       if (
  //         isBrowser &&
  //         typeof image === "object" &&
  //         image !== null &&
  //         image instanceof File
  //       ) {
  //         return URL.createObjectURL(image);
  //       }

  //       // Handle string URLs (legacy format)
  //       return image as string;
  //     })
  //     .filter((url) => url && url.trim() !== ""); // Filter out empty URLs

  //   return processedImages.length > 3
  //     ? processedImages.slice(0, 4)
  //     : processedImages;
  // }, [images]);

  // Обработка изображений
  // Обработка изображений → для buildSrcSet
  const slicedImages = useMemo<IImagePopulated[][]>(() => {
    if (!images || images.length === 0) {
      return [];
    }

    const processedImages = images
      .map((image) => {
        // ✅ Image from API (IImagesProperty)
        if (
          typeof image === "object" &&
          image !== null &&
          "populated" in image &&
          Array.isArray(image.populated)
        ) {
          return image.populated;
        }

        if (
          typeof image === "object" &&
          image !== null &&
          "variants" in image &&
          Array.isArray(image.variants)
        ) {
          return image.variants;
        }

        // 📂 File object (uploads) — srcSet не поддерживается
        if (
          isBrowser &&
          typeof image === "object" &&
          image !== null &&
          image instanceof File
        ) {
          return [
            {
              type: "original",
              format: image.type || "jpeg",
              url: URL.createObjectURL(image),
            },
          ];
        }

        // 🔗 Legacy string URL
        if (typeof image === "string") {
          return [
            {
              type: "original",
              format: "jpeg",
              url: image,
            },
          ];
        }

        return [];
      })
      .filter((variants) => variants.length > 0);

    return processedImages.length > 3
      ? processedImages.slice(0, 4)
      : processedImages;
  }, [images, isBrowser]);

  // Generate href with return path for proper back navigation
  const href = getObjectLink(`${redirectLink}/${slug}/`);

  // Generate WhatsApp message
  const whatsappMessage = `${t("Hello")}. ${t("I am interested in")} ${objectTitle} [${customId}]: ${process.env.NEXT_PUBLIC_FRONT_URL}${href}`;

  const containerClasses = useMemo(() => {
    return cn(
      "relative w-full rounded-[2.5rem] shadow shadow-gray-110/40 flex flex-col gap-7",
      view === EnumViewMode.LIST && "sm:flex-row sm:gap-9 sm:items-start",
      view === EnumViewMode.GRID &&
        "sm:max-w-full sm:max-w-screen-md sm:mx-auto",
      view === EnumViewMode.CARD &&
        "md:flex-row md:gap-9 md:items-start md:max-w-screen-xl md:mx-auto",
    );
  }, [view]);

  const imageContainerClasses = useMemo(() => {
    return cn(
      "relative overflow-hidden rounded-lg w-full aspect-[4/3]",
      view === EnumViewMode.LIST &&
        "sm:shrink-0 sm:w-5/12 sm:min-w-[30rem] sm:h-full",
      view === EnumViewMode.GRID && "sm:shrink-0 sm:w-auto",
      view === EnumViewMode.CARD && "md:shrink-0 md:w-5/12 md:min-w-[30rem]",
    );
  }, [view]);

  const imageClasses = useMemo(() => {
    return cn(
      "w-full aspect-[4/3] object-cover",
      view === EnumViewMode.LIST && "aspect-[4/3] sm:h-[30rem]",
      view === EnumViewMode.GRID && "sm:h-auto",
      view === EnumViewMode.CARD &&
        "md:aspect-auto md:h-[30rem] lg:h-[36rem] xl:h-[40rem]",
    );
  }, [view]);

  const areaDisplay = useMemo(() => {
    if (areaMin && areaMax) {
      if (Number(areaMin) === Number(areaMax)) {
        return areaMin;
      }
      return `${areaMin} – ${areaMax}`;
    }
    if (areaMin) {
      return areaMin;
    }
    if (areaMax) {
      return areaMax;
    }
    if (area !== undefined && area !== null) {
      return area;
    }
    return null;
  }, [area, areaMin, areaMax]);

  // Helper function to check if pricing data is available for current currency
  const isPricingDataAvailable = (
    priceData: ConvertedPrices | undefined,
  ): boolean => {
    if (!priceData) {
      return false;
    }
    return priceData[currency.value as keyof typeof priceData] !== undefined;
  };

  const amountVariant = useMemo(() => {
    if (dealType === DealType.BUY) {
      return AmountVariant.FULL;
    }
    if (dealType === DealType.RENT) {
      return AmountVariant.PER_MONTH;
    }
    return AmountVariant.FULL;
  }, [dealType]);

  // useEffect for checking description height (must be called before any conditional returns)
  useEffect(() => {
    const checkDescriptionHeight = () => {
      if (descriptionRef.current) {
        const element = descriptionRef.current;
        const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
        const maxHeight = lineHeight * 4; // 4 rows

        if (element.scrollHeight > maxHeight) {
          setShowMore(false);
          setShowMoreState(true);
        } else {
          setShowMore(false);
          setShowMoreState(false);
        }
      }
    };

    checkDescriptionHeight();

    // Recheck on window resize
    window.addEventListener("resize", checkDescriptionHeight);
    return () => window.removeEventListener("resize", checkDescriptionHeight);
  }, [descriptionRef, view]);

  // Context menu action handlers
  const handleActualize = async () => {
    try {
      await actualizeProperty.mutateAsync(id);
      toast.success(t("Property actualized successfully"));
    } catch (error) {
      toast.error(t("Failed to actualize property"));
    }
  };

  const handleDuplicate = async () => {
    try {
      await duplicateProperty.mutateAsync(id);
      toast.success(t("Property duplicated successfully"));
    } catch (error) {
      toast.error(t("Failed to duplicate property"));
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(
      `${process.env.NEXT_PUBLIC_FRONT_URL}${href}`,
    );
    toast.success(t("Property link copied to clipboard"));
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteProperty.mutateAsync({ propertyId: id });
      toast.success(t("Property deleted successfully"));
      setIsDeleteModalOpen(false);
    } catch (error) {
      toast.error(t("Failed to delete property"));
    }
  };

  // Helper function to render total price section
  const renderTotalPrice = (): React.ReactNode => {
    const hasRangeData =
      pricing?.convertedPricesMin &&
      pricing?.convertedPricesMax &&
      isPricingDataAvailable(pricing.convertedPricesMin) &&
      isPricingDataAvailable(pricing.convertedPricesMax);

    const hasSingleData =
      pricing?.convertedPrices &&
      isPricingDataAvailable(pricing.convertedPrices);

    if (hasRangeData) {
      return (
        <p className={"text-dark space-x-2"}>
          <span className={"text-sm"}>{t("from")}</span>
          <Amount
            className={cn(styles.gap, "text-[2.1rem] font-bold")}
            convertedPrices={pricing.convertedPricesMin}
            variant={amountVariant}
          />
        </p>
      );
    }

    if (hasSingleData) {
      return (
        <p className={"text-dark"}>
          <Amount
            className={cn(styles.gap, "text-[2.1rem] font-bold")}
            convertedPrices={pricing.convertedPrices}
            variant={amountVariant}
          />
        </p>
      );
    }

    return null;
  };

  // Helper function to render price per square meter section
  const renderPricePerSquareMeter = (): React.ReactNode => {
    const hasRangeData =
      pricing?.convertedPricesPerSqMMin &&
      pricing?.convertedPricesPerSqMMax &&
      isPricingDataAvailable(pricing.convertedPricesPerSqMMin) &&
      isPricingDataAvailable(pricing.convertedPricesPerSqMMax);

    const hasSingleData =
      pricing?.convertedPricesPerSqM &&
      isPricingDataAvailable(pricing.convertedPricesPerSqM);

    if (hasRangeData) {
      return (
        <p className={cn("text-dark space-x-2")}>
          <span className={"text-sm"}>{t("from")}</span>
          <Amount
            className={"text-[1.6rem] font-medium"}
            convertedPrices={pricing.convertedPricesPerSqMMin}
            variant={AmountVariant.PER_SQUARE}
          />
        </p>
      );
    }

    if (hasSingleData) {
      return (
        <p className={cn("text-dark")}>
          <Amount
            className={"text-[1.6rem] font-medium"}
            convertedPrices={pricing.convertedPricesPerSqM}
            variant={AmountVariant.PER_SQUARE}
          />
        </p>
      );
    }

    return null;
  };

  return (
    <li className={cn(containerClasses, "min-w-0", className)}>
      {/* контейнер для слайдера картинок */}
      <div
        className={cn(
          imageContainerClasses,
          "group relative",
          view === EnumViewMode.LIST &&
            "lg:max-h-[300px] max-lg:max-w-full max-xl:max-w-[390px]",
        )}
      >
        <Feature name={FeatureFlags.PROPERTY_EDIT_ACTION}>
          <Feature.Enabled>
            {/* Hover overlay with context menu - visible only for realtor+ roles */}
            {(hasEnhancedAccess || canArchive) && (
              <div className="absolute top-3 left-3 z-10 flex items-center gap-[12px]">
                <ObjectActionsMenu
                  objectId={id}
                  slug={slug}
                  title={objectTitle}
                  status={status}
                  userCanArchive={canArchive}
                />
                {/*  Плашка тут*/}
                {canArchive && status && <StatusBadge status={status} />}
              </div>
            )}
          </Feature.Enabled>
        </Feature>
        <CustomSlider
          main={
            <div className={cn("flex rounded-sm h-full w-full")}>
              {slicedImages.length > 0 ? (
                slicedImages.map((imageVariants, index) => {
                  const isFirstElement = index === 0;
                  const isLastElement = index === slicedImages.length - 1;
                  const imageUrl = getFallbackSrc(imageVariants);
                  const hasValidImageUrl = Boolean(imageUrl && imageUrl.trim());

                  return (
                    <Link
                      className={cn(styles.itemImages, "link")}
                      key={`${index}-${objectTitle}`}
                      href={href}
                    >
                      {isAvalibleVisa && programs?.isGoldenVisaEligible && (
                        <div className="absolute top-5 right-4">
                          <VisaIcon />
                        </div>
                      )}

                      {!hasValidImageUrl ? (
                        <FallbackImage className={imageClasses} />
                      ) : (
                        // <div className={styles.itemImages}>
                        <div className="w-full h-full flex-1">
                          <ResponsiveImage
                            // className={}
                            maxSize="tablet"
                            // sizes="(max-width: 768px) 320px, 640px"
                            images={imageVariants}
                            alt={objectTitle}
                            loading={isFirstElement ? "eager" : "lazy"}
                          />
                        </div>
                      )}

                      {isLastElement && slicedImages.length > 1 && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-r-lg">
                          <p className="text-white text-[1.6rem] font-bold">
                            {t("View all")}
                          </p>
                        </div>
                      )}
                    </Link>
                  );
                })
              ) : (
                <Link
                  key={objectTitle}
                  href={href}
                  className={cn(styles.itemImages, "link")}
                >
                  {isAvalibleVisa && programs?.isGoldenVisaEligible && (
                    <div className="absolute top-5 right-4">
                      <VisaIcon />
                    </div>
                  )}
                  <div className="h-full w-full flex-1">
                    <Image
                      className={imageClasses}
                      fill={true}
                      sizes="(max-width: 768px) 320px, 640px"
                      src={iconEmpty}
                      style={{ objectFit: "cover" }}
                      alt={objectTitle}
                    />
                  </div>
                </Link>
              )}
            </div>
          }
          length={images?.length || 0}
        />
      </div>
      <div className={cn("grow flex flex-col gap-6 p-6")}>
        <div className={cn(styles.descTop, !isFavorite && "mb-auto", "gap-4")}>
          <Link
            className={
              "overflow-hidden line-clamp-2 whitespace-pre-line break-all"
            }
            href={href}
          >
            <h2
              className={cn(
                "font-medium text-[1.8rem] wrap-anywhere break-normal hyphens-auto",
                view === EnumViewMode.LIST && "",
                view === EnumViewMode.GRID && "",
                view === EnumViewMode.CARD && "lg:text-[2.3rem]",
              )}
            >
              {objectTitle}
            </h2>
          </Link>
          <div className="flex items-center gap-2 translate-y-2 shrink-0">
            <AddToFavorite
              className={""}
              id={id}
            />
          </div>
        </div>

        {(dealType === DealType.BUY || dealType === DealType.RENT) &&
          view !== EnumViewMode.CARD &&
          objectDescription && (
            <p
              className={cn(
                "min-w-0 max-w-full text-[1.6rem] line-clamp-3 overflow-hidden text-ellipsis",
                isFavorite && "mb-auto",
              )}
            >
              {objectDescription}
            </p>
          )}

        <div className={"flex flex-wrap items-center"}>
          <LocationTitle
            city={
              typeof location === "object"
                ? Object.values(location)[0]
                : String(location)
            }
          />

          <CurrencyChanger
            classNames={{
              btn: "ms-auto",
              arrow: "text-red",
            }}
          />
        </div>

        {(dealType === DealType.BUY || dealType === DealType.RENT) && (
          <PropertyMainInfo
            className={"gap-6"}
            area={areaDisplay}
            landArea={landArea}
            bedrooms={bedrooms}
            rooms={rooms}
            floor={floor}
            floors={floors}
          />
        )}

        <div
          className={cn(
            "flex gap-x-8 gap-y-4 items-center justify-between flex-wrap",
            view === EnumViewMode.LIST && "sm:justify-between",
            view === EnumViewMode.GRID && "sm:justify-between",
            view === EnumViewMode.CARD && "sm:justify-start",
          )}
        >
          <div
            className={cn(
              "flex gap-4 items-start justify-start text-nowrap flex-col",
              view === EnumViewMode.LIST && "sm:grow",
              view === EnumViewMode.GRID && "sm:grow",
              view === EnumViewMode.CARD && "sm:flex-row sm:items-baseline",
            )}
          >
            {/* total price */}

            {renderTotalPrice()}

            {/* price per sqm */}
            {renderPricePerSquareMeter()}
          </div>
        </div>
        {dealType === DealType.PRIMARY && (
          <div
            className={cn(
              styles.container,
              styles.squareContainer,
              "space-x-4",
            )}
          >
            <IconSquare
              stroke={1}
              className={cn(styles.icon, "text-gray-1000")}
            />
            <p className={cn("space-x-4 text-[#323232] text-[1.8rem]")}>
              <span>{t("Square")}:</span>
              <span className={cn("font-bold")}>
                {areaDisplay + " " + t("m2")}
              </span>
            </p>
          </div>
        )}

        {view === EnumViewMode.CARD && objectDescription && (
          <p
            ref={descriptionRef}
            className={cn(
              "text-[1.4rem] xl:text-[1.6rem] overflow-hidden text-ellipsis transition-[max-height] duration-300 ease-in-out",
              !showMore ? "max-h-[9.6rem]" : "max-h-[1000px]",
            )}
          >
            {objectDescription}
          </p>
        )}

        {(dealType === DealType.BUY || dealType === DealType.RENT) && (
          <div className={"flex items-center gap-6 justify-start flex-wrap"}>
            <div className={cn(styles.container, styles.squareContainer)}>
              <IconId
                stroke={1}
                className={cn(styles.icon)}
              />
              <p className={cn(styles.title, styles.titleSpan)}>{customId}</p>
            </div>
            {createdAt && (
              <div className={cn(styles.container, styles.squareContainer)}>
                <IconCalendarWeek
                  stroke={1}
                  className={cn(styles.icon)}
                />
                <p className={cn(styles.title, styles.titleSpan)}>
                  {formatDate(createdAt || "")}
                </p>
              </div>
            )}
            {view === EnumViewMode.CARD && showMoreState && description && (
              <ExpandButton
                className={"ml-auto whitespace-nowrap text-[1.6rem]"}
                titleCollapsed={"Read more"}
                titleExpanded={"Read less"}
                expanded={showMore}
                setExpanded={setShowMore}
              />
            )}
          </div>
        )}

        {dealType === DealType.PRIMARY && (
          <div
            className={cn(
              "flex gap-[1rem] items-center flex-wrap justify-center",
              view === EnumViewMode.LIST &&
                "sm:justify-stretch sm:items-stretch",
              view === EnumViewMode.GRID && "sm:justify-center",
              view === EnumViewMode.CARD && "sm:justify-end",
            )}
          >
            {view === EnumViewMode.CARD && showMoreState && description && (
              <div className={"relative xl:mr-auto basis-full xl:basis-auto"}>
                <ExpandButton
                  className={
                    "ml-auto xl:ml-0 xl:w-full whitespace-nowrap text-[1.6rem]"
                  }
                  titleCollapsed={"Read more"}
                  titleExpanded={"Read less"}
                  expanded={showMore}
                  setExpanded={setShowMore}
                />
              </div>
            )}

            {/* Кнопки связи для обычных пользователей */}
            <Feature name={FeatureFlags.PROPERTY_EDIT_ACTION}>
              <Feature.Enabled>
                {!hasEnhancedAccess && (
                  <>
                    <div
                      className={cn(
                        "relative shrink grow basis-0",
                        view === EnumViewMode.GRID && "sm:basis-auto",
                        view === EnumViewMode.CARD &&
                          "sm:basis-auto sm:min-w-[14rem]",
                        view === EnumViewMode.LIST &&
                          "sm:basis-auto sm:min-w-[14rem]",
                      )}
                    >
                      <WriteButton
                        id={id}
                        buttonProps={{ size: "sm" }}
                        message={whatsappMessage}
                      />
                    </div>

                    <div
                      className={cn(
                        "relative shrink grow basis-0",
                        view === EnumViewMode.GRID && "sm:basis-auto",
                        view === EnumViewMode.CARD &&
                          "sm:basis-auto sm:min-w-[14rem]",
                        view === EnumViewMode.LIST &&
                          "sm:basis-auto sm:min-w-[14rem]",
                      )}
                    >
                      <CallButton
                        id={id}
                        buttonProps={{ size: "sm" }}
                      />
                    </div>
                  </>
                )}
              </Feature.Enabled>
              <Feature.Disabled>
                <div
                  className={cn(
                    "relative shrink grow basis-0",
                    view === EnumViewMode.GRID && "sm:basis-auto",
                    view === EnumViewMode.CARD &&
                      "sm:basis-auto sm:min-w-[14rem]",
                    view === EnumViewMode.LIST &&
                      "sm:basis-auto sm:min-w-[14rem]",
                  )}
                >
                  <WriteButton
                    id={id}
                    buttonProps={{ size: "sm" }}
                    message={whatsappMessage}
                  />
                </div>

                <div
                  className={cn(
                    "relative shrink grow basis-0",
                    view === EnumViewMode.GRID && "sm:basis-auto",
                    view === EnumViewMode.CARD &&
                      "sm:basis-auto sm:min-w-[14rem]",
                    view === EnumViewMode.LIST &&
                      "sm:basis-auto sm:min-w-[14rem]",
                  )}
                >
                  <CallButton
                    id={id}
                    buttonProps={{ size: "sm" }}
                  />
                </div>
              </Feature.Disabled>
            </Feature>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setIsDeleteModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t("Confirm Delete")}
            </h2>
            <p className="text-gray-600 mb-6">
              {t(
                "Are you sure you want to delete this property? This action cannot be undone.",
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {t("Delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};
